import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import authService from '../../../services/authService'
import type { StepData } from '../../../services/onboardingService'

// Components
import { LoadingState } from './ScheduleVisit/components/LoadingState'
import { ErrorState } from './ScheduleVisit/components/ErrorState'
import { ExistingAppointmentState } from './ScheduleVisit/components/ExistingAppointmentState'
import { ScheduleVisitForm } from './ScheduleVisit/components/ScheduleVisitForm'
import { CalendlyWidget } from './ScheduleVisit/components/CalendlyWidget'
import { AppointmentConfirmation } from './ScheduleVisit/components/AppointmentConfirmation'

// Hooks
import { useUserData } from './ScheduleVisit/hooks/useUserData'
import { useAppointmentCheck } from './ScheduleVisit/hooks/useAppointmentCheck'
import { useCalendlyIntegration } from './ScheduleVisit/hooks/useCalendlyIntegration'
import { useAppointmentData } from './ScheduleVisit/hooks/useAppointmentData'

// Types
import type {
  CalendlyEvent,
  AppointmentType,
  AppointmentConfig,
} from './ScheduleVisit/types'
import { APPOINTMENT_CONFIGS } from './ScheduleVisit/types'

interface ScheduleVisitStepProps {
  onComplete?: (stepData: StepData) => Promise<void>
  onNavigateImmediately?: () => void // Nuevo prop agregado
  stepData?: Record<string, unknown>
  stepKey?: string
  appointmentType?: AppointmentType
  config?: AppointmentConfig
}

const ScheduleVisitStep: React.FC<ScheduleVisitStepProps> = ({
  onComplete,
  onNavigateImmediately, // Recibir el prop
  appointmentType = 'visita_ocular',
  config,
}) => {
  // Use provided config or default based on appointmentType
  const appointmentConfig = config || APPOINTMENT_CONFIGS[appointmentType]
  // State
  const [isLoading, setIsLoading] = useState(false)
  const [showCalendlyWidget, setShowCalendlyWidget] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Custom hooks
  const {
    userInfo,
    isLoading: isLoadingUser,
    error,
    loadUserData,
    setError,
  } = useUserData()
  const {
    isCheckingAppointment,
    hasExistingAppointment,
    checkAppointmentAndProceed,
  } = useAppointmentCheck()
  const {
    calendlyUrl,
    initCalendlyPopupWithPrefill,
    generateCalendlyUrl,
    cleanupCalendly,
    setCalendlyUrl,
  } = useCalendlyIntegration()
  const {
    appointmentData,
    isPollingData,
    setAppointmentData,
    setIsPollingData,
    createManualAppointment,
    fetchAppointmentData,
  } = useAppointmentData()

  // Load initial data
  useEffect(() => {
    let isMounted = true

    const loadData = async () => {
      if (!isMounted) return
      // Only load user data, skip unnecessary appointment checking
      await loadUserData()
    }

    loadData()

    // Check if returning from Calendly
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('from') === 'calendly') {
      console.log('🔄 Detected return from Calendly')

      // Clean URL parameters
      window.history.replaceState({}, document.title, window.location.pathname)

      // If there's indication of completion, proceed
      if (urlParams.get('step') === 'complete') {
        setShowConfirmation(true)
        setTimeout(() => {
          fetchAppointmentData(userInfo.email, userInfo, onComplete)
        }, 3000)
      } else {
        // Check if appointment was scheduled
        setTimeout(() => {
          checkAppointmentAndProceed(
            userInfo.email,
            userInfo.primaryAddress,
            onComplete
          )
        }, 2000)
      }
    }

    // Cleanup on unmount
    return () => {
      isMounted = false
      cleanupCalendly()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen to Calendly events
  useEffect(() => {
    const handleCalendlyEvent = async (e: MessageEvent) => {
      if (e.origin !== 'https://calendly.com') return

      if (e.data.event && e.data.event.indexOf('calendly') === 0) {
        console.log('📅 Calendly event:', e.data.event, e.data.payload)

        const event: CalendlyEvent = e.data

        if (event.event === 'calendly.event_scheduled') {
          console.log('✅ Appointment scheduled - event data:', event.payload)
          toast.success('¡Cita agendada exitosamente!')

          // --- CÓDIGO ORIGINAL RE-INTEGRADO ---
          // Extraer datos básicos del evento de Calendly
          const eventData = event.payload as any
          const scheduledDate =
            eventData?.scheduled_event?.start_time || new Date().toISOString()

          // Mostrar confirmación inmediata en la UI
          setShowConfirmation(true)
          setAppointmentData({
            basic: eventData,
            scheduledAt: scheduledDate,
          })

          // Crear cita manual en el backend como fallback anterior
          console.log('🔄 Creating manual appointment in backend...')
          setIsPollingData(true)
          handleBackFromCalendly()

          try {
            // Llamada al endpoint anterior de cita manual
            await createManualAppointment({
              email: userInfo.email,
              name: userInfo.name,
              scheduledDate: scheduledDate,
              userId: userInfo.userId,
            })

            // --- NUEVO FLUJO DE FINALIZACIÓN MANUAL (Bypass Webhook) ---
            // 1. Obtener ID de la solicitud
            const creditAppId =
              await authService.getCurrentCreditApplicationId()

            if (creditAppId) {
              console.log('� Finalizando solicitud en DB manualmente...')
              const result =
                await authService.finalizeCreditApplicationManual(creditAppId)

              if (result) {
                console.log('✅ Solicitud finalizada exitosamente en DB')
                // Redirigir después de mostrar la confirmación por un momento
                setTimeout(() => {
                  const targetUrl =
                    result.creditApp?.progressURL ||
                    result.progressURL ||
                    '/onboarding/success'
                  window.location.href = targetUrl
                }, 3000) // 3 segundos para que vean el check de éxito
              }
            } else {
              // Si no hay creditAppId, intentamos el polling normal anterior
              setTimeout(async () => {
                await fetchAppointmentData(userInfo.email, userInfo, onComplete)
                setIsPollingData(false)
              }, 1000)
            }
          } catch (error) {
            console.error('Error in post-scheduling flow:', error)
            // En caso de error, intentar el flujo original como fallback último
            setTimeout(async () => {
              await fetchAppointmentData(userInfo.email, userInfo, onComplete)
              setIsPollingData(false)
            }, 2000)
          }
        } else if (event.event === 'calendly.popup_closed') {
          console.log('📱 Calendly popup closed')
          setIsLoading(false)

          // Clean modal state to avoid leaving it "open"
          setShowCalendlyWidget(false)
          setCalendlyUrl('')

          // If popup closed without showing confirmation, user can try again if needed
          // Skip automatic appointment checking
        } else if (event.event === 'calendly.popup_widget_destroyed') {
          console.log('🗑️ Calendly widget destroyed')
          // Additional cleanup
          setShowCalendlyWidget(false)
          setCalendlyUrl('')
          setIsLoading(false)
        }
      }
    }

    window.addEventListener('message', handleCalendlyEvent)
    return () => window.removeEventListener('message', handleCalendlyEvent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onComplete,
    userInfo.email,
    userInfo.name,
    userInfo.userId,
    showConfirmation,
  ])

  // Event handlers
  const handleScheduleWithCalendly = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const url = await generateCalendlyUrl(userInfo, appointmentType)
      await initCalendlyPopupWithPrefill(url, userInfo)
    } catch (error: any) {
      console.error('Error generating Calendly URL:', error)
      setError(error.message || 'Error al generar el enlace de Calendly')
      toast.error('Error al generar el enlace de Calendly')
    } finally {
      // Loading is handled by the popup opening
      setTimeout(() => setIsLoading(false), 1000)
    }
  }

  const handleContinueWithoutAppointment = async () => {
    if (onComplete) {
      await onComplete({
        completedAt: new Date().toISOString(),
        data: {
          appointmentType: appointmentType,
          skipped: true,
          reason: 'Usuario decidió continuar sin agendar cita',
        },
      })
    }
  }

  const handleBackFromCalendly = () => {
    setShowCalendlyWidget(false)
    setCalendlyUrl('')
    setError(null)
    setIsLoading(false)
    cleanupCalendly()
  }

  console.log('Appointment Data:', userInfo.primaryAddress)

  // Render states
  if (isCheckingAppointment || isLoadingUser) {
    return (
      <LoadingState
        isCheckingAppointment={isCheckingAppointment}
        isLoading={isLoadingUser}
      />
    )
  }

  if (error) {
    return <ErrorState error={error} onRetry={loadUserData} />
  }

  if (hasExistingAppointment) {
    return (
      <ExistingAppointmentState onContinue={handleContinueWithoutAppointment} />
    )
  }

  if (showCalendlyWidget) {
    return (
      <CalendlyWidget
        isLoading={isLoading}
        calendlyUrl={calendlyUrl}
        onBack={handleBackFromCalendly}
      />
    )
  }

  if (showConfirmation) {
    return (
      <AppointmentConfirmation
        appointmentData={appointmentData}
        isPollingData={isPollingData}
        userInfo={userInfo}
        onContinue={onNavigateImmediately}
      />
    )
  }

  return (
    <ScheduleVisitForm
      userInfo={userInfo}
      isLoading={isLoading}
      config={appointmentConfig}
      onScheduleWithCalendly={handleScheduleWithCalendly}
      onContinueWithoutAppointment={handleContinueWithoutAppointment}
    />
  )
}

export default ScheduleVisitStep
